import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatCircle } from "@phosphor-icons/react";
import { fmtAgo, fmtDateTime, humanize } from "./format";

/**
 * Lab Chat (admin view) — multi-party shared thread on the case.
 * Reuses the existing /api/cases/{id}/messages endpoint that the
 * doctor/planner/admin all participate in.
 */
export function LabChatTab({ c }) {
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const bottomRef = useRef(null);

    const fetch = () => {
        api.get(`/cases/${c.id}/messages`)
            .then((r) => setMessages(r.data || []))
            .catch(() => {});
    };
    useEffect(fetch, [c.id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    const send = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post(`/cases/${c.id}/messages`, { text: text.trim() });
            setText("");
            fetch();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" data-testid="chat-tab" style={{ height: "70vh" }}>
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Lab chat</h3>
                <span className="text-[10px] text-muted-foreground">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messages.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-10">
                        <ChatCircle size={28} className="mx-auto text-muted-foreground/40 mb-2" />
                        No messages yet — start the conversation with the doctor + planning team below.
                    </div>
                )}
                {messages.map((m) => {
                    const mine = m.author_role === "admin";
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[75%] p-3 rounded-2xl ${mine ? "bg-primary text-white rounded-tr-sm" : "bg-secondary rounded-tl-sm"}`} data-testid={`chat-msg-${m.id}`}>
                                <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                                    {m.author_name || "User"} · {humanize(m.author_role)} · {fmtAgo(m.created_at)}
                                </div>
                                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>
            <div className="p-3 border-t border-border bg-secondary/30 flex items-center gap-2">
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Message the lab + doctor…"
                    className="flex-1 h-10 px-3 text-sm rounded-lg border border-border bg-background"
                    data-testid="chat-input"
                />
                <button
                    onClick={send}
                    disabled={busy || !text.trim()}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-white disabled:opacity-50"
                    data-testid="chat-send"
                >
                    <PaperPlaneTilt size={16} weight="fill" />
                </button>
            </div>
        </div>
    );
}
